import { StoreOrderStep } from '@/dealers/_gen/gql'
import AccountMachine from '@/gf/modules/AccountMachine'
import AdditionalCharge from '@/gf/modules/AdditionalCharge'
import Discount from '@/gf/modules/Discount'
import LineItem from '@/gf/modules/LineItem'
import Refund from '@/gf/modules/Refund'
import StoreOrderEvent from '@/gf/modules/StoreOrderEvent'
import Time from '@/gf/modules/Time'
import Vendor from '@/gf/modules/Vendor'
import { ApolloClient, InMemoryCache, gql } from '@apollo/client'
import { jsonToGraphQLQuery } from 'json-to-graphql-query'
import nth from 'lodash/nth'
import MoneyM from '../../gf/modules/Money'
import Product from '../../gf/modules/Product'
import { address, money, pagination, storeOrderAudit } from '../../gf/queries/commonGqlTypes'
import {
  Address,
  LineItemInput,
  Money,
  MoneyPayload,
  Pagination,
  ProductPayload,
  SearchArgs,
  ShippingPayload,
  Store,
  StoreData,
  StoreOrderPayload,
  User,
} from '../../types'

const gqlClient = new ApolloClient({
  uri: `/gql`,
  cache: new InMemoryCache(),
  defaultOptions: { query: { fetchPolicy: 'no-cache' } },
})

const cancelStoreOrder = async (cancelationForm: {
  storeOrderId: string
  notes: string
  sendEmail: boolean
  reason: { id: string; details?: string } | null
}) => {
  const resp = await fetch('/api/store_order/cancel', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(cancelationForm),
  })

  if (resp.status !== 204) return null
  return 'ok'
}

const createShipment = async (params: ShippingPayload) =>
  fetch('/api/store_order/shipment', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  })

const searchStores = async ({ page = 1, filters }: SearchArgs) => {
  const data = {
    query: {
      storesSearch: {
        __args: { page, filters },
        pagination,
        stores: {
          id: true,
          name: true,
          returnPolicy: true,
          shippingInfo: true,
          description: true,
          gearflowShipping: true,
          gearflowPaymentsEnabled: true,
          shipsFree: true,
          enabled: true,
          autoQuote: true,
          showPartsHub: true,
          showExportOrders: true,
          address: {
            firstName: true,
            lastName: true,
            lineOne: true,
            lineTwo: true,
            city: true,
            state: true,
            country: true,
            postalCode: true,
            companyName: true,
          },
          users: {
            id: true,
            name: true,
            notify: true,
            role: true,
            email: true,
            confirmed: true,
          },
          phoneNumber: true,
        },
      },
    },
  }

  return gqlClient
    .query<{ storesSearch: { pagination: Pagination; stores: Store[] } }>({
      query: gql(jsonToGraphQLQuery(data, { pretty: true })),
    })
    .then((result) => result.data.storesSearch)
}

const fetchStore = async (id: string) =>
  searchStores({ filters: [['equals', 'id', id]] }).then(({ stores }) => stores[0])

const searchProducts = async ({ page = 1, filters }: SearchArgs) => {
  const data = {
    query: {
      productsSearch: {
        __args: { page, filters },
        pagination,
        products: {
          id: true,
          name: true,
          description: true,
          shortDescription: true,
          mpn: true,
          altMpn: true,
          sku: true,
          brand: { id: true, name: true },
          category: { id: true, name: true },
          salePrice: true,
          listPrice: true,
          shippingCost: true,
          quantity: true,
          availability: true,
          leadTime: true,
          enabled: true,
          offline: true,
          images: {
            variants: {
              name: true,
              url: true,
            },
          },
          addedAt: true,
          updatedAt: true,
          shippingRate: true,
          path: true,
          store: {
            id: true,
            name: true,
          },
          sourceProduct: {
            name: true,
            mpn: true,
            altMpn: true,
            sku: true,
            shippingCost: {
              amount: true,
              currency: true,
            },
            shortDescription: true,
            longDescription: true,
            availability: true,
            leadTime: true,
            source: {
              name: true,
            },
          },
          fitsWithMachines: {
            make: true,
            model: true,
            year: true,
          },
        },
      },
    },
  }

  return gqlClient
    .query<{ productsSearch: { pagination: Pagination; products: ProductPayload[] } }>({
      query: gql(jsonToGraphQLQuery(data, { pretty: true })),
    })
    .then(({ data: { productsSearch } }) => ({
      ...productsSearch,
      products: productsSearch.products.map(Product.fromPayload),
    }))
}

const fetchProduct = async (id: string) =>
  searchProducts({ filters: [['equals', 'id', id]] }).then(({ products }) => products[0])

const searchStoreOrdersLite = async ({ page = 1, filters }: SearchArgs) => {
  return gqlClient.query<{
    storeOrdersSearchLite: { pagination: { totalResults: number } }
  }>({
    query: gql(
      jsonToGraphQLQuery(
        {
          query: {
            storeOrdersSearchLite: {
              __args: { page, filters },
              pagination: { totalResults: true },
            },
          },
        },
        { pretty: true }
      )
    ),
  })
}

type StoreOrdersSearchPayload = {
  pagination: Pagination
  storeOrders: (Omit<StoreOrderPayload, 'order'> & {
    step: StoreOrderStep
    order: Omit<StoreOrderPayload['order'], 'user'> & {
      user: {
        id: string
        displayName: string
        email: string
        phoneNumber: string | null
      }
    }
  })[]
}

const parseStoreOrdersSearch = (storeOrdersSearch: StoreOrdersSearchPayload) => {
  return {
    ...storeOrdersSearch,
    storeOrders: storeOrdersSearch.storeOrders.map((storeOrder) => {
      return {
        ...storeOrder,
        shippingAmount: storeOrder.shippingAmount && MoneyM.fromPayload(storeOrder.shippingAmount),
        salesTax: storeOrder.salesTax && MoneyM.fromPayload(storeOrder.salesTax),
        insertedAt:
          storeOrder.insertedAt !== undefined ? Time.fromPayload(storeOrder.insertedAt) : undefined,
        updatedAt:
          storeOrder.updatedAt !== undefined ? Time.fromPayload(storeOrder.updatedAt) : undefined,
        total: storeOrder.total !== undefined ? MoneyM.fromPayload(storeOrder.total) : undefined,
        lineItems: storeOrder.lineItems && storeOrder.lineItems.map(LineItem.fromPayload),
        additionalCharges:
          storeOrder.additionalCharges &&
          storeOrder.additionalCharges.map(AdditionalCharge.fromPayload),
        refunds: storeOrder.refunds && storeOrder.refunds.map(Refund.fromPayload),
        discounts: storeOrder.discounts && storeOrder.discounts.map(Discount.fromPayload),
        events: storeOrder.events && storeOrder.events.map(StoreOrderEvent.fromPayload),
        accountMachines:
          storeOrder.accountMachines &&
          storeOrder.accountMachines.map((accountMachinePayload) =>
            AccountMachine.fromPayload(accountMachinePayload)
          ),
        vendor: storeOrder.vendor && Vendor.fromPayload(storeOrder.vendor),
      }
    }),
  }
}

export type StoreOrdersSearch = ReturnType<typeof parseStoreOrdersSearch>

const searchStoreOrders = async ({ page = 1, filters }: SearchArgs) => {
  const data = {
    query: {
      storeOrdersSearch: {
        __args: { page, filters },
        pagination,
        storeOrders: {
          id: true,
          shortId: true,
          insertedAt: true,
          updatedAt: true,
          state: true,
          step: true,
          deliveryMethod: true,
          paymentStatus: true,
          paymentMethod: true,
          shippingAmount: money,
          salesTax: money,
          total: money,
          convenienceFeeRate: true,
          gearflowShipping: true,
          purchaseOrder: true,
          payOnInvoice: true,
          timingDetails: true,
          pickup: true,
          externalOrder: true,
          pickupAddress: {
            firstName: true,
            lastName: true,
            companyName: true,
            city: true,
            state: true,
            country: true,
            lineOne: true,
            lineTwo: true,
            postalCode: true,
          },
          shippingAddress: {
            firstName: true,
            lastName: true,
            lineOne: true,
            lineTwo: true,
            city: true,
            state: true,
            country: true,
            postalCode: true,
            companyName: true,
            deliverable: true,
            rdi: true,
          },
          vendor: {
            accountNumbers: true,
            contactName: true,
            emailAddress: true,
            phoneNumber: true,
          },
          accountMachines: {
            id: true,
            name: true,
            serialNumber: true,
            owned: true,
            engineMake: true,
            engineModel: true,
            engineSerialNumber: true,
            machine: {
              make: true,
              model: true,
              year: true,
            },
          },
          store: {
            id: true,
            name: true,
            address: {
              firstName: true,
              lastName: true,
              lineOne: true,
              lineTwo: true,
              city: true,
              state: true,
              country: true,
              postalCode: true,
              companyName: true,
            },
            email: true,
            phoneNumber: true,
            shipsFree: true,
            gearflowPaymentsEnabled: true,
          },
          order: {
            id: true,
            name: true,
            shortId: true,
            insertedAt: true,
            updatedAt: true,
            state: true,
            phoneNumber: true,
            email: true,
            comment: true,
            user: {
              id: true,
              displayName: true,
              email: true,
              phoneNumber: true,
            },
            billingAddress: {
              firstName: true,
              lastName: true,
              lineOne: true,
              lineTwo: true,
              city: true,
              state: true,
              country: true,
              postalCode: true,
              companyName: true,
            },
            requestForQuote: {
              id: true,
              fullName: true,
              phoneNumber: true,
              emailAddress: true,
              partsRequest: true,
              machineInformation: true,
              shippingOption: true,
            },
          },
          balanceTransaction: { id: true },
          additionalCharges: {
            id: true,
            name: true,
            price: money,
            state: true,
          },
          discounts: {
            percent: true,
          },
          refunds: {
            id: true,
            amount: money,
          },
          lineItems: {
            id: true,
            quantity: true,
            inStock: true,
            availableAt: true,
            externalId: true,
            taskNumber: true,
            suggestion: true,
            product: {
              id: true,
              name: true,
              mpn: true,
              altMpn: true,
              salePrice: true,
              images: {
                variants: {
                  name: true,
                  url: true,
                },
              },
            },
            unitPrice: money,
            discountedUnitPrice: money,
            extendedPrice: money,
            shipmentItems: {
              id: true,
              quantity: true,
              shipment: {
                id: true,
                link: true,
                carrier: true,
                tracking: true,
                shippedAt: true,
                label: {
                  id: true,
                  url: true,
                  cost: money,
                  shipDate: true,
                },
              },
            },
          },
          events: {
            id: true,
            type: true,
            privacy: true,
            user: {
              id: true,
              displayName: true,
            },
            insertedAt: true,
            transaction: {
              id: true,
              action: true,
              customer: money,
              gearflow: money,
              supplier: money,
            },
            refund: {
              id: true,
              amount: money,
            },
            additionalCharge: {
              id: true,
              name: true,
              price: money,
            },
            shipment: {
              id: true,
              shipmentItems: {
                id: true,
                quantity: true,
                lineItem: {
                  id: true,
                  productName: true,
                  quantity: true,
                },
              },
            },
            storeOrderAudit,
            diffStoreOrderAudit: storeOrderAudit,
            note: true,
            emailSent: true,
            data: {
              id: true,
              details: true,
              payOnAccount: true,
              payOnInvoice: true,
              previousAddress: address,
            },
          },
          features: {
            txns: true,
            storeOrderTimelineV1: true,
          },
          buyerNotificationUsers: {
            id: true,
            name: true,
          },
        },
      },
    },
  }

  return gqlClient
    .query<{ storeOrdersSearch: StoreOrdersSearchPayload }>({
      query: gql(jsonToGraphQLQuery(data, { pretty: true })),
    })
    .then(({ data: { storeOrdersSearch } }) => parseStoreOrdersSearch(storeOrdersSearch))
}

const searchUsers = async ({ page = 1, filters }: SearchArgs) => {
  const data = {
    query: {
      usersSearch: {
        __args: { page, filters },
        pagination,
        users: {
          id: true,
          name: true,
          email: true,
          notify: true,
          role: true,
          shippingAddress: {
            firstName: true,
            lastName: true,
            lineOne: true,
            lineTwo: true,
            city: true,
            state: true,
            postalCode: true,
            country: true,
            companyName: true,
            deliverable: true,
            rdi: true,
          },
          billingAddress: {
            firstName: true,
            lastName: true,
            lineOne: true,
            lineTwo: true,
            city: true,
            state: true,
            postalCode: true,
            country: true,
            companyName: true,
          },
          phoneNumber: true,
        },
      },
    },
  }

  return gqlClient
    .query<{ usersSearch: { pagination: Pagination; users: User[] } }>({
      query: gql(jsonToGraphQLQuery(data, { pretty: true })),
    })
    .then((result) => result.data.usersSearch)
}

const fetchUser = async (id: string) =>
  searchUsers({ filters: [['equals', 'id', id]] }).then(({ users }) => users[0])

const fetchStoreOrder = async (storeOrderId: string) =>
  searchStoreOrders({
    filters: [['equals', 'id', storeOrderId]],
  }).then(({ storeOrders }) => nth(storeOrders, 0) || null)

const fetchStoreOrderForOrder = async (orderId: string, storeId: string) =>
  searchStoreOrders({
    filters: [['and'], ['equals', 'orderId', orderId], ['equals', 'storeId', storeId]],
  }).then(({ storeOrders }) => storeOrders[0])

const sendOrderEditEmail = async (storeOrderId: string, notes: string) =>
  fetch('/api/store_order/send_edit_mail', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ storeOrderId, notes }),
  })

const createOfflineProduct = async (name: string, price: string, mpn: string, storeId?: string) =>
  fetch('/api/product/create_offline_product', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name, price, mpn, storeId }),
  })

const refundStoreOrder = async (
  storeOrderId: string,
  amount: Money,
  notes: string,
  changeStoreOrderState: boolean,
  sendEmail: boolean
) => {
  const response = await fetch('/api/store_order/refund', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      storeOrderId,
      amount: MoneyM.toDecimal(amount),
      notes,
      changeStoreOrderState,
      sendEmail,
    }),
  })

  if (response.status === 204) return 'success'

  if (response.status === 400) {
    const json = await response.json()
    return { message: json.message as string }
  }

  return { message: 'Sorry, something went wrong trying to refund this order.' }
}

const updateStoreAddressSettings = async (id: string, data: Address, phoneNumber: string) =>
  fetch('/api/store/update_address', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id, ...data, phoneNumber }),
  })

const updateStorePolicySettings = async (id: string, data: StoreData) =>
  fetch('/api/store/update_policies', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ id, ...data }),
  })

const addAdditionalCharge = async (
  storeOrderId: string,
  name: string,
  price: number,
  sendEmail: boolean,
  emailNotes: string
) => {
  const response = await fetch('/api/store_order/additional_charge', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ storeOrderId, name, price, sendEmail, emailNotes }),
  })
  const unknownErrorMessage = 'Sorry, something went wrong adding the additional charge.'

  if (response.status === 204) return 'success'

  if (response.status === 400) {
    const json = await response.json()
    return {
      message: (json.message || unknownErrorMessage) as string,
    }
  }

  return { message: unknownErrorMessage }
}

const calculateShippingCost = async (storeId: string, lineItemInputs: LineItemInput[]) =>
  gqlClient
    .query<{ calculateShippingCost: { shippingCost: MoneyPayload } }>({
      query: gql`
        query CalculateShippingCost($storeId: String, $lineItemInputs: [LineItemInput]) {
          calculateShippingCost(storeId: $storeId, lineItemInputs: $lineItemInputs) {
            shippingCost {
              amount
              currency
            }
          }
        }
      `,
      variables: {
        storeId,
        lineItemInputs,
      },
    })
    .then(
      ({
        data: {
          calculateShippingCost: { shippingCost },
        },
      }) => MoneyM.fromPayload(shippingCost)
    )

export {
  addAdditionalCharge,
  calculateShippingCost,
  cancelStoreOrder,
  createOfflineProduct,
  createShipment,
  fetchProduct,
  fetchStore,
  fetchStoreOrder,
  fetchStoreOrderForOrder,
  fetchUser,
  refundStoreOrder,
  searchProducts,
  searchStoreOrders,
  searchStoreOrdersLite,
  sendOrderEditEmail,
  updateStoreAddressSettings,
  updateStorePolicySettings,
}
